import { Form, Formik, useFormikContext } from 'formik';
import { Fragment, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { Divider } from '@hivebrite/design-system/Divider';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import type { DonationItem } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import { requiresBillingPage } from 'hb-react/shared/apps/Events/pages/Booking/pages/Registration/utils';
import { money } from 'hb-react/shared/apps/Memberships/utils/money';
import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import ErrorFocus from 'hb-react/shared/components/formik/components/ErrorFocus';
import { Number as NumberInput } from 'hb-react/shared/components/formik/components/inputs';
import { useAmplitude } from 'hb-react/shared/hooks/useAmplitude';
import { numberWithCurrency } from 'hb-react/shared/utils/formatNumber';
import { validateMinimum } from 'hb-react/shared/utils/validators/minimum';

const hasDonation = ({ donations = [] }: { donations: DonationItem[] }) =>
  donations
    .map(({ cartItem }) => Number(cartItem?.total))
    .reduce((donation, total) => donation + total, 0) > 0;

const Footer = ({ onClose }: { onClose: () => void }) => {
  const { values, isSubmitting, isValid, submitForm } = useFormikContext<{
    donations: DonationItem[];
  }>();

  const donated = hasDonation(values);

  const buttonText = donated ? __('Yes, submit donation') : __('No, thanks');

  return (
    <Box justifyContent="flex-end" mt={6}>
      <Button
        onClick={() => onClose()}
        type="button"
        variant="secondary"
        mr={2}
      >
        {__('Go back')}
      </Button>
      <Button
        onClick={() => submitForm()}
        isDisabled={!isValid}
        isLoading={isSubmitting}
      >
        {buttonText}
      </Button>
    </Box>
  );
};

const DonationRow = ({
  currentUser,
  name,
  title,
  description,
  minimum_amount: minimumAmount,
  currency,
}) => {
  const placeholder =
    minimumAmount > 0 && currency
      ? `${__('Min.')} ${numberWithCurrency(minimumAmount, currency, {
          locale: currentUser?.locale || I18n.locale,
          currencyDisplay: 'narrowSymbol',
        })}`
      : '0';

  const minimum = minimumAmount > 0 ? minimumAmount : 0;

  return (
    <Box flexDirection="column">
      <Box font="h4-semibold">{title}</Box>
      {description && <Box mt={2}>{description}</Box>}
      <Box mt={3} alignItems="flex-start">
        <NumberInput
          ariaLabel={s__('AccessibilityEvents|Amount for %{donationTitle}').i__(
            {
              donationTitle: title,
            },
          )}
          name={name}
          placeholder={placeholder}
          validate={{
            // @ts-expect-error: fix types
            customValidator: (value) => {
              if (Number(value) === 0) {
                return;
              }

              return validateMinimum(value, minimum);
            },
            // @ts-expect-error: fix types
            maximumNumberOfDecimals: currency.has_cents ? 2 : 0,
          }}
          wrapperProps={{
            mb: 0,
            width: 170,
          }}
        />
      </Box>
    </Box>
  );
};

export const DonationsPage = () => {
  const { currentUser } = useDefaultAppContexts();
  const navigate = useNavigate();
  const { trackEvent } = useAmplitude();
  const {
    donations = [],
    actions,
    metadata,
    cart,
    isLoading,
    event,
  } = useBooking();

  useEffect(() => {
    if (metadata?.donations_displayed || isLoading) {
      return;
    }

    actions.reset({
      metadata: {
        donations_displayed: true,
      },
    });
  }, [metadata?.donations_displayed, isLoading]);

  const onClose = () => {
    navigate(-1);
  };

  const initialValues = {
    donations: donations.map((donation) => ({
      ...donation,
      cartItem: {
        total: '',
        ...donation.cartItem,
      },
    })),
  };

  if (donations.length === 0) {
    return null;
  }

  const validate = ({ donations }) => {
    const errors = {};

    donations.forEach((donation, i) => {
      const isInvalid =
        donation.amount !== 0 &&
        validateMinimum(donation.amount, donation.minimum_amount);

      if (isInvalid) {
        errors[i] = isInvalid;
      }
    });

    return errors;
  };

  return (
    <ModalWithTemplate
      header={
        event?.donation_overlay_headline ??
        __('Would you like to add a donation onto your order?')
      }
      width="medium"
      screenReadersCloseButtonText={s__(
        'NetworkEventsAccessibility|Close add a donation onto your order window',
      )}
      onCloseModal={onClose}
    >
      <Formik
        initialValues={initialValues}
        validate={validate}
        enableReinitialize
        onSubmit={async ({ donations = [] }) => {
          donations
            .filter(
              (donation) =>
                'cartItem' in donation &&
                !!donation.cartItem?.id &&
                Number(donation.cartItem.total) <= 0,
            )
            .forEach((donation) => {
              if (donation?.cartItem?.id)
                actions.items.remove(donation.cartItem.id);
            });

          const donated = donations.filter(
            (donation) =>
              !!donation.cartItem && Number(donation.cartItem.total) > 0,
          );

          if (donated.length === 0) {
            trackEvent('NetworkEvents|User skips donation');

            if (
              (cart!.total_in_cents && cart!.total_in_cents > 0) ||
              requiresBillingPage(cart)
            ) {
              return navigate('../../billing');
            }

            if (cart!.total_in_cents === 0) {
              await actions.complete();

              return navigate('../../success');
            }
          }

          const response = await actions.reset({
            donations: donated.map(({ id, cartItem, currency }) => ({
              line_itemable_type: 'Donation::Campaign',
              line_itemable_id: id,
              quantity: 1,
              currency,
              total_in_cents: money(Number(cartItem.total), currency?.code)
                .cents,
              metadata: {
                event: {
                  type: 'donation',
                },
              },
            })),
          });

          if (!response) {
            return;
          }

          const { data } = response;

          trackEvent('NetworkEvents|User adds a donation', {
            cart_total: data.cart.total,
          });

          if (data.cart.total_in_cents && data.cart.total_in_cents > 0) {
            navigate('../../billing');
          }

          if (data.cart.total_in_cents === 0) {
            await actions.complete();

            return navigate('../../success');
          }
        }}
      >
        <StyledForm>
          <ErrorFocus />
          {donations.map((donation, index) => (
            <Fragment key={donation.id}>
              <Box display="block">
                <DonationRow
                  currentUser={currentUser}
                  {...donation}
                  name={`donations[${index}][cartItem][total]`}
                />
              </Box>
              {index < donations.length - 1 && <Divider />}
            </Fragment>
          ))}
          <Footer onClose={onClose} />
        </StyledForm>
      </Formik>
    </ModalWithTemplate>
  );
};

const StyledForm = styled(Form)`
  width: 100%;
`;
